// Main Airbolt SDK client implementation
import type {
  AirboltConfig,
  ChatParams,
  ChatResult,
  StreamParams,
  StreamChunk,
  FetchOptions,
  RequestOptions,
} from "./types";
import { AirboltError, isAirboltError } from "./errors";
import {
  createHeaders,
  fetchWithRetry,
  validateProjectId,
  debugLog,
  safeJsonParse,
  getDefaultBaseUrl,
} from "./utils";
import { parseVercelStream } from "./stream";

/**
 * Internal configuration with required and optional fields
 */
type NormalizedAirboltConfig = Required<Omit<AirboltConfig, "endUserId">> &
  Pick<AirboltConfig, "endUserId">;

/**
 * Main Airbolt SDK client for chat completions and streaming
 */
export class Airbolt {
  private config: NormalizedAirboltConfig;

  constructor(config: AirboltConfig) {
    this.config = this.validateAndNormalizeConfig(config);
  }

  /**
   * Validates and normalizes the configuration
   */
  private validateAndNormalizeConfig(
    config: AirboltConfig,
  ): NormalizedAirboltConfig {
    // Validate project ID
    if (!config.projectId || typeof config.projectId !== "string") {
      throw new AirboltError(
        "projectId is required and must be a string",
        "CONFIG_ERROR",
      );
    }

    if (!validateProjectId(config.projectId)) {
      throw new AirboltError(
        "Invalid projectId format. Expected format: proj_[alphanumeric]",
        "CONFIG_ERROR",
      );
    }

    // Handle base URL
    const baseUrl = config.baseUrl || getDefaultBaseUrl();

    // Validate and normalize base URL
    let normalizedBaseUrl: string;
    try {
      const url = new URL(baseUrl);
      if (url.protocol !== "https:" && url.protocol !== "http:") {
        throw new Error("Base URL must use HTTP or HTTPS protocol");
      }
      normalizedBaseUrl = url.toString().replace(/\/$/, "");
    } catch (error) {
      throw new AirboltError(`Invalid base URL: ${baseUrl}`, "CONFIG_ERROR");
    }

    return {
      projectId: config.projectId,
      baseUrl: normalizedBaseUrl,
      debug: config.debug || false,
      ...(config.endUserId !== undefined && { endUserId: config.endUserId }),
    };
  }

  /**
   * Comprehensive validation for chat parameters
   */
  private validateEssentialParams(params: ChatParams): void {
    if (!params || typeof params !== "object") {
      throw new AirboltError(
        "params is required and must be an object",
        "VALIDATION_ERROR",
        400,
      );
    }

    if (!params.model) {
      throw new AirboltError("model is required", "VALIDATION_ERROR", 400);
    }

    if (!params.messages) {
      throw new AirboltError("messages is required", "VALIDATION_ERROR", 400);
    }

    // Validate messages is non-empty array
    if (!Array.isArray(params.messages)) {
      throw new AirboltError(
        "messages must be an array",
        "VALIDATION_ERROR",
        400,
      );
    }

    if (params.messages.length === 0) {
      throw new AirboltError(
        "messages must be a non-empty array",
        "VALIDATION_ERROR",
        400,
      );
    }

    // Validate each message structure
    for (let i = 0; i < params.messages.length; i++) {
      const message = params.messages[i];

      if (!message || typeof message !== "object") {
        throw new AirboltError(
          `messages[${i}] must be an object`,
          "VALIDATION_ERROR",
          400,
        );
      }

      if (!message.role) {
        throw new AirboltError(
          `messages[${i}] role is required`,
          "VALIDATION_ERROR",
          400,
        );
      }

      if (!["system", "user", "assistant"].includes(message.role)) {
        throw new AirboltError(
          `messages[${i}] role must be one of: system, user, assistant`,
          "VALIDATION_ERROR",
          400,
        );
      }

      if (!message.content || typeof message.content !== "string") {
        throw new AirboltError(
          `messages[${i}] content is required and must be a string`,
          "VALIDATION_ERROR",
          400,
        );
      }

      if (message.content.trim().length === 0) {
        throw new AirboltError(
          `messages[${i}] content cannot be empty`,
          "VALIDATION_ERROR",
          400,
        );
      }
    }

    // Validate optional parameters
    if (params.temperature !== undefined) {
      if (
        typeof params.temperature !== "number" ||
        params.temperature < 0 ||
        params.temperature > 2
      ) {
        throw new AirboltError(
          "temperature must be a number between 0 and 2",
          "VALIDATION_ERROR",
          400,
        );
      }
    }

    if (params.maxTokens !== undefined) {
      if (!Number.isInteger(params.maxTokens) || params.maxTokens < 1) {
        throw new AirboltError(
          "maxTokens must be a positive integer",
          "VALIDATION_ERROR",
          400,
        );
      }
    }
  }

  /**
   * Sends a chat completion request and returns the full response
   *
   * @param params - Chat completion parameters
   * @param options - Optional request configuration including abort signal
   * @returns Promise that resolves to the chat completion result
   */
  async chat(
    params: ChatParams,
    options?: RequestOptions,
  ): Promise<ChatResult> {
    debugLog(this.config.debug, "Starting chat completion request", {
      model: params.model,
    });

    // Basic validation - server handles detailed validation
    this.validateEssentialParams(params);

    // Create request headers
    const headers = createHeaders(this.config.projectId, this.config.endUserId);

    // Create endpoint URL
    const url = `${this.config.baseUrl}/api/v1/chat`;

    // Create request body matching the API schema
    const requestBody = {
      model: params.model,
      messages: params.messages,
      ...(params.systemPrompt && { systemPrompt: params.systemPrompt }),
      ...(params.temperature !== undefined && {
        temperature: params.temperature,
      }),
      ...(params.maxTokens !== undefined && { max_tokens: params.maxTokens }),
    };

    debugLog(this.config.debug, "Chat request", {
      url,
      model: params.model,
      messageCount: params.messages.length,
      streaming: false,
      hasSystemPrompt: !!params.systemPrompt,
      temperature: params.temperature,
      maxTokens: params.maxTokens,
    });

    try {
      // Make the request
      const response = await fetchWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
        ...(options?.signal && { signal: options.signal }),
        ...(options?.fetchOptions && { fetchOptions: options.fetchOptions }),
      });

      // Parse response
      const responseText = await response.text();
      const result = safeJsonParse<ChatResult>(responseText);

      if (!result) {
        throw new AirboltError(
          "Invalid JSON response from server",
          "VALIDATION_ERROR",
          400,
        );
      }

      debugLog(this.config.debug, "Chat completion successful", {
        id: result.id,
        model: result.model,
        usage: result.usage,
      });

      return result;
    } catch (error) {
      debugLog(this.config.debug, "Chat completion failed:", error);

      // Re-throw Airbolt errors as-is
      if (isAirboltError(error)) {
        throw error;
      }

      // Wrap unexpected errors
      throw new AirboltError(
        `Unexpected error during chat completion: ${error instanceof Error ? error.message : "Unknown error"}`,
        "UNEXPECTED_ERROR",
      );
    }
  }

  /**
   * Sends a streaming chat completion request and returns an async generator
   *
   * @param params - Chat completion parameters (same as chat())
   * @param options - Optional request configuration including abort signal
   * @returns AsyncGenerator that yields streaming chunks
   */
  async *stream(
    params: StreamParams,
    options?: RequestOptions,
  ): AsyncGenerator<StreamChunk, void, unknown> {
    debugLog(this.config.debug, "Starting streaming chat completion request", {
      model: params.model,
    });

    // Basic validation - server handles detailed validation
    this.validateEssentialParams(params);

    // Create request headers
    const headers = createHeaders(this.config.projectId, this.config.endUserId);

    // Create endpoint URL for streaming
    const url = `${this.config.baseUrl}/api/v1/chat/stream`;

    // Create request body matching the API schema (note: no explicit stream parameter needed)
    const requestBody = {
      model: params.model,
      messages: params.messages,
      ...(params.systemPrompt && { systemPrompt: params.systemPrompt }),
      ...(params.temperature !== undefined && {
        temperature: params.temperature,
      }),
      ...(params.maxTokens !== undefined && { max_tokens: params.maxTokens }),
    };

    debugLog(this.config.debug, "Streaming chat request", {
      url,
      model: params.model,
      messageCount: params.messages.length,
      streaming: true,
      hasSystemPrompt: !!params.systemPrompt,
      temperature: params.temperature,
      maxTokens: params.maxTokens,
    });

    try {
      // Make the streaming request
      const response = await fetchWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
        ...(options?.signal && { signal: options.signal }),
        ...(options?.fetchOptions && { fetchOptions: options.fetchOptions }),
      });

      // Ensure we got a successful response
      if (!response.body) {
        throw new AirboltError(
          "No response body received from streaming endpoint",
          "VALIDATION_ERROR",
          400,
        );
      }

      debugLog(
        this.config.debug,
        "Streaming response received, starting to parse",
      );

      // API now uses Vercel AI SDK, so we expect Vercel format
      const parser = parseVercelStream(response.body, this.config.debug);

      // Parse the streaming response
      let chunkCount = 0;

      for await (const chunk of parser) {
        chunkCount++;
        debugLog(this.config.debug, `Yielding streaming chunk ${chunkCount}`);
        yield chunk;
      }

      debugLog(
        this.config.debug,
        `Streaming completed. Processed ${chunkCount} chunks.`,
      );
    } catch (error) {
      debugLog(this.config.debug, "Streaming chat completion failed:", error);

      // Re-throw Airbolt errors as-is
      if (isAirboltError(error)) {
        throw error;
      }

      // Wrap unexpected errors
      throw new AirboltError(
        `Unexpected error during streaming chat completion: ${error instanceof Error ? error.message : "Unknown error"}`,
        "UNEXPECTED_ERROR",
      );
    }
  }

  /**
   * Gets the current configuration (read-only)
   */
  getConfig(): Readonly<NormalizedAirboltConfig> {
    return { ...this.config };
  }

  /**
   * Updates the debug flag
   */
  setDebug(debug: boolean): void {
    this.config.debug = debug;
    debugLog(debug, `Debug logging ${debug ? "enabled" : "disabled"}`);
  }

  /**
   * Tests the connection to the Airbolt API
   * Makes a GET request to the chat endpoint to verify connectivity
   */
  async testConnection(
    options?: RequestOptions,
  ): Promise<{ status: string; version?: string }> {
    debugLog(this.config.debug, "Testing connection to Airbolt API");

    const headers = createHeaders(this.config.projectId);
    const url = `${this.config.baseUrl}/api/v1/chat`;

    try {
      const response = await fetchWithRetry(url, {
        method: "GET",
        headers,
        ...(options?.signal && { signal: options.signal }),
        ...(options?.fetchOptions && { fetchOptions: options.fetchOptions }),
      });

      const responseText = await response.text();
      const result = safeJsonParse(responseText);

      debugLog(this.config.debug, "Connection test successful:", result);

      return {
        status: "connected",
        ...(result &&
          typeof result === "object" &&
          "version" in result && { version: result.version }),
      };
    } catch (error) {
      debugLog(this.config.debug, "Connection test failed:", error);
      throw error;
    }
  }
}
